/**
 * Responsibility: Build normalized action-envelope and execution-context contracts.
 * Scope: Contract shaping only; action resolution and mutation orchestration stay elsewhere.
 */
import type {
  Ability,
  ActionCategory,
  ActionTargetRef,
  ActionToolRef,
  ActionEnvelope,
  ActionExecutionContext,
  ActionMode,
  ActionSource,
  GameState,
} from "@anyquest/shared-core"
import { ActionMode as ActionModeEnum, ActionSource as ActionSourceEnum } from "@anyquest/shared-core"
import { extractTargetName } from './combat'
import { formatClock, formatDay } from './temporal'

type ActionActorInput =
  | { kind: 'player'; name?: undefined }
  | { kind: 'companion'; name: string }
  | { kind: 'npc'; name: string; id?: string }
  | { kind: 'system'; label?: string; id?: string }

type ExplicitTargetInput = {
  kind: ActionTargetRef['kind']
  label: string
  id?: string
}

type ExplicitToolInput = {
  kind: ActionToolRef['kind']
  label: string
  id?: string
}

type BuildActionEnvelopeOptions = {
  source?: ActionSource
  mode?: ActionMode
  category?: ActionCategory
  actor?: ActionActorInput
  ability?: Ability
  argsText?: string
  explicitTargetName?: string
  explicitTargets?: ExplicitTargetInput[]
  tool?: ExplicitToolInput
  eventId?: string
  eventTitle?: string
  tags?: string[]
}

function toRefId(value: string): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function normalizeText(value: string | number | boolean | null | undefined): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

function buildActorRef(gs: GameState, actor: ActionActorInput) {
  if (actor.kind === 'npc') {
    const roomNpc = (gs.currentRoom.npcs ?? []).find(entry => entry.name.toLowerCase() === actor.name.toLowerCase())
    const worldNpc = (gs.world.npcs ?? []).find(entry => entry.name.toLowerCase() === actor.name.toLowerCase())
    const label = String(roomNpc?.name ?? worldNpc?.name ?? actor.name).trim() || 'NPC'
    return {
      kind: 'npc' as const,
      id: String(actor.id ?? worldNpc?.id ?? toRefId(label) ?? 'npc'),
      label,
    }
  }

  if (actor.kind === 'system') {
    const label = String(actor.label ?? 'System').trim() || 'System'
    return {
      kind: 'system' as const,
      id: String(actor.id ?? toRefId(label) ?? 'system'),
      label,
    }
  }

  if (actor.kind === 'companion') {
    const companion = (gs.companions ?? []).find(entry => entry.name.toLowerCase() === actor.name.toLowerCase())
    const label = String(companion?.name ?? actor.name).trim() || 'Companion'
    return {
      kind: 'companion' as const,
      id: toRefId(label) || 'companion',
      label,
    }
  }

  const label = String(gs.player.name ?? gs.player.identity ?? 'Player').trim() || 'Player'
  return {
    kind: 'player' as const,
    id: 'player',
    label,
  }
}

function resolveTargetLabels(
  gs: GameState,
  input: string,
  options: Pick<BuildActionEnvelopeOptions, 'ability' | 'argsText' | 'explicitTargetName'>,
): string[] {
  const labels = new Set<string>()
  const explicit = String(options.explicitTargetName ?? '').trim()
  const attackTarget = extractTargetName(input)
  const argsText = String(options.argsText ?? '').trim().toLowerCase()
  const candidates = [
    ...(gs.currentRoom.npcs ?? []).map(npc => ({ kind: 'npc' as const, label: npc.name })),
    ...(gs.companions ?? []).map(companion => ({ kind: 'companion' as const, label: companion.name })),
    { kind: 'player' as const, label: String(gs.player.name ?? gs.player.identity ?? 'Player').trim() || 'Player' },
  ]

  for (const raw of [explicit, attackTarget, gs.combat?.activeTargetName]) {
    const wanted = String(raw ?? '').trim().toLowerCase()
    if (!wanted) continue
    const match = candidates.find(candidate => candidate.label.toLowerCase().includes(wanted) || wanted.includes(candidate.label.toLowerCase()))
    if (match?.label) labels.add(match.label)
  }

  if (options.ability && argsText) {
    for (const candidate of candidates) {
      const lowered = candidate.label.toLowerCase()
      if (lowered && argsText.includes(lowered)) labels.add(candidate.label)
    }
  }

  return [...labels]
}

function toTargetRef(target: ExplicitTargetInput): ActionTargetRef {
  return {
    kind: target.kind,
    id: String(target.id ?? toRefId(target.label) ?? target.kind),
    label: String(target.label ?? '').trim() || target.kind,
  }
}

function deriveTargets(
  gs: GameState,
  input: string,
  options: Pick<BuildActionEnvelopeOptions, 'ability' | 'argsText' | 'explicitTargetName' | 'explicitTargets' | 'eventId' | 'eventTitle' | 'source'>,
): ActionTargetRef[] {
  const refs = Array.isArray(options.explicitTargets)
    ? options.explicitTargets.map(target => toTargetRef(target))
    : resolveTargetLabels(gs, input, options).map(label => {
        const roomNpc = (gs.currentRoom.npcs ?? []).find(npc => npc.name === label)
        if (roomNpc) {
          return { kind: 'npc' as const, id: toRefId(roomNpc.name) || 'npc', label: roomNpc.name }
        }
        const companion = (gs.companions ?? []).find(entry => entry.name === label)
        if (companion) {
          return { kind: 'companion' as const, id: toRefId(companion.name) || 'companion', label: companion.name }
        }
        const playerLabel = String(gs.player.name ?? gs.player.identity ?? 'Player').trim() || 'Player'
        if (label === playerLabel) {
          return { kind: 'player' as const, id: 'player', label: playerLabel }
        }
        return { kind: 'npc' as const, id: toRefId(label) || 'target', label }
      })

  if ((options.source === ActionSourceEnum.event || options.eventId || options.eventTitle) && !refs.some(target => target.kind === 'event')) {
    const eventId = String(options.eventId ?? gs.activeEvent?.id ?? '').trim()
    const eventLabel = String(options.eventTitle ?? gs.activeEvent?.title ?? 'Active Event').trim() || 'Active Event'
    refs.unshift({
      kind: 'event',
      id: eventId || toRefId(eventLabel) || 'event',
      label: eventLabel,
    })
  }

  return refs
}

function deriveTool(
  options: Pick<BuildActionEnvelopeOptions, 'tool' | 'ability' | 'category' | 'source'>,
): ActionToolRef | undefined {
  if (options.tool) {
    return {
      kind: options.tool.kind,
      id: String(options.tool.id ?? toRefId(options.tool.label) ?? options.tool.kind),
      label: String(options.tool.label ?? '').trim() || options.tool.kind,
    }
  }

  if (options.ability) {
    return {
      kind: 'ability',
      id: toRefId(options.ability.id || options.ability.name) || 'ability',
      label: String(options.ability.name ?? 'Ability').trim() || 'Ability',
    }
  }

  if (options.category === 'attack') {
    return { kind: 'attack', id: 'attack', label: 'Attack' }
  }

  if (options.source === ActionSourceEnum.event) {
    return { kind: 'free-text', id: 'event_input', label: 'Event Input' }
  }

  return undefined
}

export function buildActionEnvelope(
  gs: GameState,
  input: string,
  options: BuildActionEnvelopeOptions = {},
): ActionEnvelope {
  const actor = buildActorRef(gs, options.actor ?? { kind: 'player' })
  const tags = new Set<string>(Array.isArray(options.tags) ? options.tags.filter(Boolean) : [])
  if (gs.combat?.status === 'active') tags.add('combat')
  if (gs.activeEvent) tags.add('active_event')
  if (actor.kind === 'companion') tags.add('companion_action')
  if (options.source === ActionSourceEnum.event) tags.add('event_action')
  if (options.source === ActionSourceEnum.system) tags.add('system_action')
  if (options.source === ActionSourceEnum.npc) tags.add('npc_action')

  const tool = deriveTool(options)
  const targets = deriveTargets(gs, input, {
    ability: options.ability,
    argsText: options.argsText,
    explicitTargetName: options.explicitTargetName,
    explicitTargets: options.explicitTargets,
    eventId: options.eventId,
    eventTitle: options.eventTitle,
    source: options.source,
  })

  const args: Record<string, string> = {}
  const rawArgs = String(options.argsText ?? '').trim()
  if (rawArgs) args.raw = rawArgs
  const explicitTargetName = String(options.explicitTargetName ?? '').trim()
  if (explicitTargetName) args.target = explicitTargetName
  const eventId = String(options.eventId ?? '').trim()
  if (eventId) args.eventId = eventId

  return {
    source: options.source ?? (actor.kind === 'companion' ? ActionSourceEnum.companion : ActionSourceEnum.player),
    mode: options.mode ?? ActionModeEnum.do,
    input: String(input ?? '').trim(),
    ...(options.category ? { category: options.category } : {}),
    actor,
    targets,
    ...(tool ? { tool } : {}),
    ...(Object.keys(args).length ? { args } : {}),
    ...(tags.size ? { tags: [...tags] } : {}),
  }
}

export function buildActionExecutionContext(
  gs: GameState,
  action: ActionEnvelope,
  roll: number,
  mechanics?: ActionExecutionContext['mechanics'],
): ActionExecutionContext {
  return {
    action,
    actorLabel: action.actor.label,
    targetLabels: action.targets.map(target => target.label),
    roomName: String(gs.currentRoom?.name ?? 'Unknown').trim() || 'Unknown',
    roomDesc: String(gs.currentRoom?.desc ?? '').trim(),
    worldTime: `${formatDay(gs.world.clock)}, ${formatClock(gs.world.clock)}`,
    inCombat: gs.combat?.status === 'active',
    roll,
    ...(mechanics ? { mechanics } : {}),
  }
}

export function buildActionCacheKey(gs: GameState, action: ActionEnvelope): string {
  const roomX = Number.isFinite(gs.player?.location?.x) ? String(gs.player.location.x) : 'x'
  const roomY = Number.isFinite(gs.player?.location?.y) ? String(gs.player.location.y) : 'y'
  const roomName = toRefId(String(gs.currentRoom?.name ?? '').trim()) || 'room'
  const roomScope = `${roomX}_${roomY}:${roomName}`
  const eventScope = normalizeText(action.targets.find(target => target.kind === 'event')?.id ?? gs.activeEvent?.id ?? '')
  const combatScope = gs.combat?.status === 'active' ? 'combat' : 'peace'
  const normalizedTargets = [...(action.targets ?? [])]
    .map(target => `${target.kind}:${normalizeText(target.id || target.label)}`)
    .filter(Boolean)
    .sort()
  const normalizedTags = [...new Set((action.tags ?? []).map(tag => normalizeText(tag)).filter(Boolean))].sort()
  const normalizedArgs = Object.entries(action.args ?? {})
    .map(([key, value]) => `${normalizeText(key)}=${normalizeText(value)}`)
    .sort()

  return [
    `src=${normalizeText(action.source)}`,
    `mode=${normalizeText(action.mode)}`,
    `cat=${normalizeText(action.category ?? 'other')}`,
    `actor=${action.actor.kind}:${normalizeText(action.actor.id || action.actor.label)}`,
    `tool=${action.tool ? `${action.tool.kind}:${normalizeText(action.tool.id || action.tool.label)}` : 'none'}`,
    `targets=${normalizedTargets.join(',') || 'none'}`,
    `room=${roomScope}`,
    `ctx=${combatScope}${eventScope ? `:event:${eventScope}` : ''}`,
    `tags=${normalizedTags.join(',') || 'none'}`,
    `args=${normalizedArgs.join(',') || 'none'}`,
    `input=${normalizeText(action.input)}`,
  ].join('|').slice(0, 512)
}
